import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import * as XLSX from 'xlsx';

interface ExcelUploaderProps {
  onFileUploaded: (data: any, filename: string) => void;
  isLoading?: boolean;
}

const ExcelUploader: React.FC<ExcelUploaderProps> = ({ onFileUploaded, isLoading }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = (file: File) => {
    if (!file) return;

    // Validate file type
    const validTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    '.xlsx',
    '.xls'];


    const isValidType = validTypes.some((type) =>
    file.type === type || file.name.toLowerCase().endsWith(type)
    );

    if (!isValidType) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {// 10MB limit
      setError('File size must be less than 10MB');
      return;
    }

    setError('');
    processExcelFile(file);
  };

  const processExcelFile = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, {
          type: 'array',
          cellFormula: true, // Preserve formulas
          cellStyles: true,
          sheetStubs: true
        });

        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        if (!worksheet) {
          setError('No data found in the Excel file');
          return;
        }

        // Convert to JSON with range to preserve empty cells
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
        const jsonData: any[][] = [];
        const columnDefinitions: any[] = [];

        // Process each row
        for (let row = range.s.r; row <= range.e.r; row++) {
          const rowData: any[] = [];
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            const cell = worksheet[cellAddress];

            if (cell) {
              // Check if cell has a formula
              if (cell.f) {
                rowData.push({
                  value: cell.v || '',
                  formula: cell.f,
                  isFormula: true
                });
              } else {
                rowData.push({
                  value: cell.v || '',
                  isFormula: false
                });
              }
            } else {
              rowData.push({
                value: '',
                isFormula: false
              });
            }
          }
          jsonData.push(rowData);
        }

        // Create column definitions
        if (jsonData.length > 0) {
          const headerRow = jsonData[0];
          headerRow.forEach((cell, index) => {
            columnDefinitions.push({
              field: `col_${index}`,
              headerName: cell.value || `Column ${index + 1}`,
              editable: true,
              flex: 1,
              minWidth: 100
            });
          });
        }

        const processedData = {
          data: jsonData,
          columnDefinitions,
          sheetName: firstSheetName,
          totalRows: jsonData.length,
          totalCols: jsonData[0]?.length || 0
        };

        console.log('Processed Excel data:', processedData);
        onFileUploaded(processedData, file.name);

      } catch (err) {
        console.error('Error processing Excel file:', err);
        setError('Failed to process Excel file. Please check the file format.');
      }
    };

    reader.onerror = () => {
      setError('Failed to read the file');
    };

    reader.readAsArrayBuffer(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  return (
    <Card className="w-full" data-id="l5z2absvq" data-path="src/components/grades/ExcelUploader.tsx">
      <CardHeader data-id="sid6uzj88" data-path="src/components/grades/ExcelUploader.tsx">
        <CardTitle className="flex items-center gap-2" data-id="vusa1d799" data-path="src/components/grades/ExcelUploader.tsx">
          <FileSpreadsheet className="h-5 w-5" data-id="4hailx865" data-path="src/components/grades/ExcelUploader.tsx" />
          Upload Grade Sheet
        </CardTitle>
        <CardDescription data-id="4oxdhwmpj" data-path="src/components/grades/ExcelUploader.tsx">
          Upload an Excel file (.xlsx or .xls) containing student grades
        </CardDescription>
      </CardHeader>
      <CardContent data-id="ba268igo8" data-path="src/components/grades/ExcelUploader.tsx">
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragOver ?
          'border-primary bg-primary/5' :
          'border-gray-300 hover:border-gray-400'}`
          }
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave} data-id="srglwtg02" data-path="src/components/grades/ExcelUploader.tsx">

          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" data-id="su1b6vpw6" data-path="src/components/grades/ExcelUploader.tsx" />
          <p className="text-lg font-medium mb-2" data-id="1s5gkz3gh" data-path="src/components/grades/ExcelUploader.tsx">
            Drop your Excel file here, or click to browse
          </p>
          <p className="text-sm text-gray-500 mb-4" data-id="nq6e9a7im" data-path="src/components/grades/ExcelUploader.tsx">
            Supports .xlsx and .xls files up to 10MB
          </p>
          
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="mb-4" data-id="jodeooj14" data-path="src/components/grades/ExcelUploader.tsx">

            {isLoading ? 'Processing...' : 'Select File'}
          </Button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
            className="hidden" data-id="kw2v2v82w" data-path="src/components/grades/ExcelUploader.tsx" />

        </div>

        {error &&
        <Alert variant="destructive" className="mt-4" data-id="wgx6n2vta" data-path="src/components/grades/ExcelUploader.tsx">
            <AlertCircle className="h-4 w-4" data-id="ldaxoxjyg" data-path="src/components/grades/ExcelUploader.tsx" />
            <AlertDescription data-id="0ol7fn6ue" data-path="src/components/grades/ExcelUploader.tsx">{error}</AlertDescription>
          </Alert>
        }
      </CardContent>
    </Card>);

};

export default ExcelUploader;